import { createContext, useContext, useState, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const TourContext = createContext({
  active: false,
  startTour: () => {},
  stopTour: () => {},
  primedAudioRef: { current: null },
});

export const TourProvider = ({ children }) => {
  const [active, setActive] = useState(false);
  // When a user chose "Resume", we want SampleDataTour to pick up from the saved step.
  // When "Start" or "Replay", we force step 0.
  const [resumeMode, setResumeMode] = useState(false);
  // A silent audio element primed inside the initial user-gesture click.
  // Keeping a ref to an unlocked HTMLAudioElement lets us swap its `src`
  // later (when the real narration base64 arrives) and keep autoplay rights.
  const primedAudioRef = useRef(null);

  const _primeAudio = useCallback(() => {
    try {
      if (primedAudioRef.current) return primedAudioRef.current;
      // 0.1s of silent MP3 (base64) — unlocks audio playback on iOS/Safari/Chrome
      const silentMp3 =
        'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQwAAAAAAAAAAAAAAAAAAAAAAASW5mbwAAAA8AAAACAAABIADAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwMDAwP/7kGQAD/AAAGkAAAAIAAANIAAAAQAAAaQAAAAgAAA0gAAABExBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV';
      const a = new Audio(silentMp3);
      a.volume = 0;
      a.play().then(() => { a.pause(); a.currentTime = 0; a.volume = 1; }).catch(() => {});
      primedAudioRef.current = a;
      return a;
    } catch { return null; }
  }, []);

  const startTour = useCallback(async (opts = {}) => {
    // MUST be called from inside a user-gesture handler so audio is unlocked.
    _primeAudio();
    if (opts.reset) {
      try { await axios.post(`${API}/api/tour/reset`); } catch {}
      setResumeMode(false);
    } else {
      setResumeMode(!!opts.resume);
    }
    setActive(true);
  }, [_primeAudio]);

  const stopTour = useCallback(() => { setActive(false); setResumeMode(false); }, []);

  // Memoized so consumers don't re-render unless one of the actual values changes.
  const value = useMemo(
    () => ({ active, resumeMode, startTour, stopTour, primedAudioRef }),
    [active, resumeMode, startTour, stopTour]
  );

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
};

export const useTour = () => useContext(TourContext);
